import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import { useTranslation } from 'react-i18next';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../../i18n/constants/keys';
import { PreQualQuestion } from '../../../../../../../utils/types/JobOpenings';
import { SelectOptions } from '../../../../../../../utils/types/react_select_types';
import { PreQualQuestionType } from '../interfaces';
import { QuestionItem } from './QuestionItem';
import { QuestionEditForm } from './QuestionEditForm';
import { getQuestionTypeLabel } from '../functions';

interface QuestionListProps {
  questions: PreQualQuestion[];
  isLoading: boolean;
  editIndex: string | null;
  editQuestionText: string;
  setEditQuestionText: (text: string) => void;
  editQuestionType: PreQualQuestionType;
  setEditQuestionType: (type: PreQualQuestionType) => void;
  questionTypeOptions: SelectOptions[];
  editChoices: string[];
  setEditChoices: React.Dispatch<React.SetStateAction<string[]>>;
  onChoiceChange: (index: number, value: string, edit: boolean) => void;
  onSaveQuestion: () => Promise<void>;
  onCancelEdit: () => void;
  onEditQuestion: (question: PreQualQuestion) => void;
  onDeleteQuestion: (key: string) => Promise<void>;
}

export const QuestionList = ({
  questions,
  isLoading,
  editIndex,
  editQuestionText,
  setEditQuestionText,
  editQuestionType,
  setEditQuestionType,
  questionTypeOptions,
  editChoices,
  setEditChoices,
  onChoiceChange,
  onSaveQuestion,
  onCancelEdit,
  onEditQuestion,
  onDeleteQuestion,
}: QuestionListProps) => {
  const { t } = useTranslation(NS.adminPages);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Section Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 0.5,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            color: 'text.secondary',
            fontSize: '0.7rem',
          }}
        >
          {t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.questionList)}
        </Typography>

        <Chip
          size="small"
          label={t(
            ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.questionsDefined,
            { count: questions.length },
          )}
          sx={{
            height: 22,
            fontSize: '0.7rem',
            fontWeight: 600,
            bgcolor: 'grey.100',
            color: 'text.secondary',
            borderRadius: '6px',
          }}
        />
      </Box>

      {/* Loading State */}
      {isLoading ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 6,
            gap: 2,
          }}
        >
          <CircularProgress size={32} thickness={4} />
          <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            {t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.loading)}
          </Typography>
        </Box>
      ) : questions.length === 0 ? (
        /* Empty State */
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 5,
            px: 2,
            borderRadius: '12px',
            bgcolor: 'grey.50',
            border: '1px dashed',
            borderColor: 'divider',
            textAlign: 'center',
            gap: 1,
          }}
        >
          <HelpOutlineRoundedIcon sx={{ fontSize: 36, color: 'text.disabled' }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
            No screening questions defined yet
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled', maxWidth: 360 }}>
            Create custom questions above or import standardized questions from the question bank.
          </Typography>
        </Box>
      ) : (
        /* Question Items List */
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {questions.map((question, index) => {
            const isEditing = question.questionKey === editIndex;

            return (
              <Box
                key={question.questionKey || index}
                sx={{
                  p: 2.25,
                  borderRadius: '12px',
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: isEditing ? 'primary.main' : 'slate.200',
                  boxShadow: isEditing
                    ? '0 0 0 4px rgba(99, 102, 241, 0.08), 0 4px 12px rgba(99, 102, 241, 0.08)'
                    : '0 1px 3px rgba(0, 0, 0, 0.02)',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    borderColor: isEditing ? 'primary.main' : 'primary.light',
                    boxShadow: isEditing
                      ? '0 0 0 4px rgba(99, 102, 241, 0.08)'
                      : '0 4px 12px rgba(15, 23, 42, 0.05)',
                    '& .question-actions': {
                      opacity: 1,
                    },
                  },
                }}
              >
                {isEditing ? (
                  <QuestionEditForm
                    editQuestionText={editQuestionText}
                    setEditQuestionText={setEditQuestionText}
                    editQuestionType={editQuestionType}
                    setEditQuestionType={setEditQuestionType}
                    questionTypeOptions={questionTypeOptions}
                    editChoices={editChoices}
                    setEditChoices={setEditChoices}
                    onChoiceChange={onChoiceChange}
                    onSaveQuestion={onSaveQuestion}
                    onCancelEdit={onCancelEdit}
                  />
                ) : (
                  <QuestionItem
                    question={question}
                    index={index}
                    typeLabel={getQuestionTypeLabel(
                      question.questionType,
                      questionTypeOptions,
                    )}
                    onEdit={onEditQuestion}
                    onDelete={onDeleteQuestion}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};
